import type { DraftStep, ValidationIssue } from "@zworks/console-contracts";
import * as React from "react";
import {
  calculateFlowLayout,
  CANVAS_PADDING,
  HORIZONTAL_GAP,
  NODE_HEIGHT,
  NODE_WIDTH,
} from "@/lib/flow-layout";
import { CanvasControls } from "./canvas-controls";
import { CanvasEdges } from "./canvas-edges";
import { CanvasGrid } from "./canvas-grid";
import { CanvasMinimap } from "./canvas-minimap";
import { CanvasNode } from "./canvas-node";
import { NodePalette, type NodeTemplate } from "./node-palette";
import type { CapabilityOption } from "./step-inspector";

export interface FlowCanvasProps {
  steps: DraftStep[];
  selectedId: string | null;
  issues: ValidationIssue[];
  tools: CapabilityOption[];
  skills: CapabilityOption[];
  canEdit: boolean;
  onSelectStep: (id: string) => void;
  onKeyDown: (event: React.KeyboardEvent, index: number) => void;
  dragProps: (index: number) => Record<string, unknown>;
  onAddTemplateStep: (
    template: NodeTemplate,
    insertIndex?: number,
    positionCoord?: { x: number; y: number },
  ) => void;
  onConnectSteps?: (fromId: string, toId: string, kind: "next" | "approve" | "reject") => void;
  onMoveStepTo?: (id: string, position: { x: number; y: number }) => void;
  customPositions?: Record<string, { x: number; y: number }>;
}

export function FlowCanvas({
  steps,
  selectedId,
  issues,
  tools,
  skills,
  canEdit,
  onSelectStep,
  onKeyDown,
  dragProps,
  onAddTemplateStep,
  onConnectSteps,
  onMoveStepTo,
  customPositions = {},
}: FlowCanvasProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Pan & Zoom state
  const [panX, setPanX] = React.useState(0);
  const [panY, setPanY] = React.useState(0);
  const [scale, setScale] = React.useState(1.0);
  const [isPanning, setIsPanning] = React.useState(false);
  const dragStartRef = React.useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Live drag positions merge over the editor-owned drop placements.
  const [draggedPositions, setDraggedPositions] = React.useState<
    Record<string, { x: number; y: number }>
  >({});
  const effectivePositions = React.useMemo(
    () => ({ ...customPositions, ...draggedPositions }),
    [customPositions, draggedPositions],
  );
  const [draggingNodeId, setDraggingNodeId] = React.useState<string | null>(null);
  const nodeDragOffsetRef = React.useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const [connecting, setConnecting] = React.useState<{
    fromId: string;
    kind: "next" | "approve" | "reject";
  } | null>(null);

  // Viewport size
  const [dimensions, setDimensions] = React.useState({ width: 1000, height: 700 });
  const hasAutoFittedRef = React.useRef(false);

  React.useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const w = containerRef.current.clientWidth || 1000;
        const h = containerRef.current.clientHeight || 700;
        setDimensions({ width: w, height: h });
      }
    };
    updateDimensions();

    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined" && containerRef.current) {
      observer = new ResizeObserver(() => {
        updateDimensions();
      });
      observer.observe(containerRef.current);
    }

    window.addEventListener("resize", updateDimensions);
    return () => {
      window.removeEventListener("resize", updateDimensions);
      observer?.disconnect();
    };
  }, []);

  // Compute node layouts with custom positions
  const layouts = React.useMemo(
    () => calculateFlowLayout(steps, effectivePositions),
    [steps, effectivePositions],
  );

  // Fit view helper
  const fitView = React.useCallback(() => {
    if (steps.length === 0) {
      setPanX(40);
      setPanY(40);
      setScale(1.0);
      return;
    }

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    layouts.forEach((layout) => {
      minX = Math.min(minX, layout.position.x);
      maxX = Math.max(maxX, layout.position.x + layout.position.width);
      minY = Math.min(minY, layout.position.y);
      maxY = Math.max(maxY, layout.position.y + layout.position.height);
    });

    if (!Number.isFinite(minX)) {
      minX = CANVAS_PADDING;
      maxX = CANVAS_PADDING + steps.length * (NODE_WIDTH + HORIZONTAL_GAP);
      minY = CANVAS_PADDING;
      maxY = CANVAS_PADDING + NODE_HEIGHT + 100;
    }

    const totalWidth = maxX - minX + CANVAS_PADDING * 2;
    const totalHeight = maxY - minY + CANVAS_PADDING * 2;

    const scaleX = (dimensions.width - 60) / totalWidth;
    const scaleY = (dimensions.height - 60) / totalHeight;
    const nextScale = Math.min(Math.max(Math.min(scaleX, scaleY), 0.4), 1.15);

    setScale(nextScale);
    setPanX(
      Math.max(
        (dimensions.width - totalWidth * nextScale) / 2 - (minX - CANVAS_PADDING) * nextScale,
        20,
      ),
    );
    setPanY(
      Math.max(
        (dimensions.height - totalHeight * nextScale) / 2 - (minY - CANVAS_PADDING) * nextScale,
        20,
      ),
    );
  }, [steps.length, dimensions.width, dimensions.height, layouts]);

  // Auto-fit on initial load or step change
  React.useEffect(() => {
    if (!hasAutoFittedRef.current && steps.length > 0 && dimensions.width > 300) {
      hasAutoFittedRef.current = true;
      fitView();
    }
  }, [steps.length, dimensions.width, fitView]);

  // Zoom handlers
  const handleZoom = (delta: number) => {
    setScale((prev) => Math.min(Math.max(prev + delta, 0.25), 2.0));
  };

  const handleResetZoom = () => {
    setScale(1.0);
    setPanX(40);
    setPanY(40);
  };

  // Mouse wheel zoom
  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const zoomFactor = e.deltaY < 0 ? 0.08 : -0.08;
      handleZoom(zoomFactor);
    } else {
      // Pan with trackpad
      setPanX((prev) => prev - e.deltaX);
      setPanY((prev) => prev - e.deltaY);
    }
  };

  // Node Drag Handler (Dragging node freely on the canvas)
  const handleNodeMouseDown = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    onSelectStep(id);
    setDraggingNodeId(id);

    const layout = layouts.get(id);
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || !layout) return;

    const mouseCanvasX = (e.clientX - rect.left - panX) / scale;
    const mouseCanvasY = (e.clientY - rect.top - panY) / scale;

    nodeDragOffsetRef.current = {
      x: mouseCanvasX - layout.position.x,
      y: mouseCanvasY - layout.position.y,
    };
  };

  // Mouse pan handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    // Only pan if clicking on empty canvas or middle click
    if (
      e.button === 1 ||
      e.target === containerRef.current ||
      (e.target as HTMLElement).tagName === "svg"
    ) {
      setIsPanning(true);
      dragStartRef.current = { x: e.clientX - panX, y: e.clientY - panY };
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (draggingNodeId) {
      // Dragging a node on the canvas
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const mouseCanvasX = (e.clientX - rect.left - panX) / scale;
      const mouseCanvasY = (e.clientY - rect.top - panY) / scale;

      const newX = mouseCanvasX - nodeDragOffsetRef.current.x;
      const newY = mouseCanvasY - nodeDragOffsetRef.current.y;

      setDraggedPositions((prev) => ({
        ...prev,
        [draggingNodeId]: { x: Math.round(newX), y: Math.round(newY) },
      }));
      return;
    }

    if (isPanning) {
      setPanX(e.clientX - dragStartRef.current.x);
      setPanY(e.clientY - dragStartRef.current.y);
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
    if (draggingNodeId) {
      const position = effectivePositions[draggingNodeId];
      if (position) onMoveStepTo?.(draggingNodeId, position);
    }
    setDraggingNodeId(null);
    setConnecting(null);
  };

  // Drag and Drop from NodePalette onto Canvas
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    try {
      const rawJson =
        e.dataTransfer.getData("application/json") || e.dataTransfer.getData("text/plain");
      if (rawJson) {
        const template = JSON.parse(rawJson) as NodeTemplate;
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
          const dropCanvasX = (e.clientX - rect.left - panX) / scale - 150;
          const dropCanvasY = (e.clientY - rect.top - panY) / scale - 40;

          onAddTemplateStep(template, undefined, {
            x: Math.round(dropCanvasX),
            y: Math.round(dropCanvasY),
          });
        } else {
          onAddTemplateStep(template);
        }
      }
    } catch {
      // Ignore non-json drag events
    }
  };

  return (
    <div
      ref={containerRef}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onKeyDown={(event) => {
        if (event.key === "Escape") setConnecting(null);
      }}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className="relative size-full min-h-[500px] overflow-hidden rounded-xl border bg-muted/20 select-none shadow-inner"
    >
      {/* Background Dot Mesh Grid */}
      <CanvasGrid panX={panX} panY={panY} scale={scale} />

      {/* Floating Node Template Library Palette (Docked nicely) */}
      <NodePalette
        canEdit={canEdit}
        className="absolute top-4 left-4"
        onAddStep={(tmpl) => onAddTemplateStep(tmpl)}
      />

      {connecting && canEdit ? (
        <p
          role="status"
          className="absolute left-1/2 top-4 z-20 -translate-x-1/2 rounded-md border border-primary/40 bg-card/95 px-3 py-1.5 text-xs font-medium shadow-lg backdrop-blur-md"
        >
          Connecting{connecting.kind === "reject" ? " reject route" : ""}: choose the target node,
          or press Escape to cancel.
        </p>
      ) : null}

      {/* Floating View Controls HUD */}
      <CanvasControls
        scale={scale}
        onZoomIn={() => handleZoom(0.15)}
        onZoomOut={() => handleZoom(-0.15)}
        onResetZoom={handleResetZoom}
        onFitView={fitView}
      />

      {/* Picture-in-Picture Minimap */}
      <CanvasMinimap
        steps={steps}
        layouts={layouts}
        panX={panX}
        panY={panY}
        scale={scale}
        containerWidth={dimensions.width}
        containerHeight={dimensions.height}
        onNavigate={(targetX, targetY) => {
          setPanX(targetX);
          setPanY(targetY);
        }}
      />

      {/* Transformable Canvas Layer (Nodes + Edges) */}
      <div
        style={{
          transform: `translate3d(${panX}px, ${panY}px, 0) scale(${scale})`,
          transformOrigin: "0 0",
        }}
        className="absolute inset-0 size-full pointer-events-auto"
      >
        {/* Semantic HTML List required for accessibility & test contract */}
        <div role="list" aria-label="Canvas" className="relative size-full">
          {steps.map((step, index) => {
            const layout = layouts.get(step.id);
            if (!layout) return null;

            return (
              <CanvasNode
                key={step.id}
                step={step}
                allSteps={steps}
                layout={layout}
                isSelected={step.id === selectedId}
                issues={issues.filter((issue) => issue.stepId === step.id)}
                tools={tools}
                skills={skills}
                canEdit={canEdit}
                onSelect={() => onSelectStep(step.id)}
                onKeyDown={(e) => onKeyDown(e, index)}
                dragProps={dragProps(index)}
                onNodeMouseDown={handleNodeMouseDown}
                connectingFrom={connecting?.fromId ?? null}
                onStartConnection={(id, kind) => {
                  if (canEdit) setConnecting({ fromId: id, kind });
                }}
                onFinishConnection={(id) => {
                  if (connecting && connecting.fromId !== id) {
                    onConnectSteps?.(connecting.fromId, id, connecting.kind);
                  }
                  setConnecting(null);
                }}
              />
            );
          })}
        </div>

        {/* SVG Bezier Edges Layer (Rendered on top with pointer-events-none) */}
        <CanvasEdges steps={steps} layouts={layouts} />
      </div>
    </div>
  );
}
